import * as THREE from "three";
import { TransformControls } from './transformcontrols.js';
import { BaseScene } from './basescene';
import { CameraManager } from './camera-manager';
import { ThreeUtils } from './threeutils';
import { GizmoOptions, ViewportGizmo } from "three-viewport-gizmo";
import * as TWEEN from '@tweenjs/tween.js';
import { CameraFlight } from './camera-flight';

export class SceneManager {
    public scene: THREE.Scene;
    public camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    public renderer: THREE.WebGLRenderer;
    public controls: any;
    public baseScene: BaseScene;
    public cameraManager: CameraManager;
    public cameraFlight: CameraFlight;
    public viewportGizmo: ViewportGizmo;
    public transformControl: any;
    public clock: THREE.Clock;
    public stats: any;
    public composer: any;
    public viewHelper: any;

    // Camera state
    private initialFrustumSize: number | null = null;
    private previousCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera | null = null;

    // Callback for proximity effects and other per-frame updates
    public onUpdateFrame: (() => void) | null = null;

    constructor(
        private rendererContainer: any,
        public project: any,
        private isMobile: boolean = false
    ) {
        this.scene = new THREE.Scene();
        this.scene.name = "root";
        this.clock = new THREE.Clock();
        this.scene.fog = new THREE.Fog(0x000000, 7000, 20000);
    }

    setupthreejs() {
        var threecontainer = document.getElementById("threecontainer");
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, transparent: true } as any);
        this.rendererContainer.nativeElement.appendChild(this.renderer.domElement);

        this.renderer.setSize(threecontainer.clientWidth, threecontainer.clientHeight);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.autoUpdate = true;
        this.renderer.shadowMap.needsUpdate = false;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        this.cameraManager = new CameraManager(this.scene, this.renderer, threecontainer);
        this.camera = this.cameraManager.camera;
        this.controls = this.cameraManager.controls;

        this.camera.position.z = 20;
        this.camera.position.x = 20;
        this.camera.position.y = 20;

        this.camera.up.set(0, 0, 1);

        this.baseScene = new BaseScene(this.scene, this.renderer);
    }

    initview(draggingChangedCallback?: (event: any) => void) {
        // https://github.com/mrdoob/three.js/blob/master/examples/misc_controls_transform.html
        const geometry = new THREE.BoxGeometry(200, 200, 200);
        const material = new THREE.MeshLambertMaterial({ transparent: false, opacity: 0.9, color: 0x00ff00 });
        const mesh = new THREE.Mesh(geometry, material);
        this.transformControl = new TransformControls(this.camera, this.renderer.domElement);

        this.transformControl.addEventListener('dragging-changed', (event) => {
            console.log("dragging-changed", event, this.transformControl.object);
            if (draggingChangedCallback) {
                draggingChangedCallback(event);
            }
        });

        this.transformControl.reset();
        this.transformControl.setSpace("local");
        this.transformControl.setMode('translate');

        this.cameraFlight = new CameraFlight(this.camera, this.controls, this.scene, null); // toastr would need to be injected
    }

    setupViewportGizmo(threecontainerwidth: number) {
        var goptions: GizmoOptions = {
            container: document.querySelector('.viewportGizmoContainer') as HTMLElement,
        }
        this.viewportGizmo = new ViewportGizmo(this.camera, this.renderer, goptions);
        this.viewportGizmo.target = this.controls.target;

        var ended = false;

        this.viewportGizmo.addEventListener("start", () => {
            ended = false;
        });

        this.viewportGizmo.addEventListener("end", () => {
            ended = true;
            this.updateThreeFrame();
            console.log("end")
        });

        this.viewportGizmo.addEventListener("change", () => {
            if (!ended) {
                console.log("change");
                this.renderer.render(this.scene, this.camera);
                this.viewportGizmo.render()
            } else {
                this.camera.updateProjectionMatrix();
            }
        });

        this.controls.addEventListener("change", () => {
            this.viewportGizmo?.update();
            this.viewportGizmo?.render();
        });
    }

    updateThreeFrame() {
        try {
            // Call any registered per-frame update callbacks (like proximity effects)
            if (this.onUpdateFrame) {
                this.onUpdateFrame();
            }

            if (!this.project?.sceneSettings?.showAnimations) {
                this.controls.enableDamping = false;

                var delta = this.clock.getDelta();
                this.controls.update(delta);
                this.camera.updateProjectionMatrix();

                // Ensure all objects are updated, especially important on mobile
                this.scene.traverse((object) => {
                    if (object.matrixAutoUpdate) {
                        object.updateMatrix();
                    }

                    // Check if the object is a sprite
                    if (object instanceof THREE.Sprite) {
                        // Update the sprite's matrix
                        object.updateMatrix();
                    }
                });

                // Force renderer to use high performance mode on mobile
                if (this.isMobile) {
                    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
                }

                this.renderer.render(this.scene, this.camera);
                this.viewportGizmo?.update();
                this.viewportGizmo?.render();
            } else {
                this.controls.enableDamping = true;
            }
        } catch (error) {
            console.warn("updateThreeFrame", error);
        }
    }

    animate(time: number) {
        if (!this.scene) return;
        requestAnimationFrame(this.animate.bind(this));

        // Update face center sprite scales based on camera distance
        this.scene.traverse((object) => {
            if (object.userData.type === "refpoint" && object instanceof THREE.Sprite) {
                // Get world position of sprite for accurate distance calculation
                const worldPosition = new THREE.Vector3();
                object.getWorldPosition(worldPosition);

                // Get distance from camera to sprite world position
                const distance = this.camera.position.distanceTo(worldPosition);

                // Scale based on distance with adjusted base size
                const scale = Math.max(distance * 0.02, 5);  // Minimum scale of 5 to ensure visibility
                object.scale.set(scale, scale, 1);
            }
        });

        // Rest of animate code...
        if (this.stats) this.stats.begin();
        if (this.controls) this.controls.update();
        if (this.transformControl) this.transformControl.update();

        // Update camera flight animations
        if (this.cameraFlight) {
            // Update TWEEN animations with timestamp
            TWEEN.update(time);
            this.cameraFlight.update();

            // Debug camera position during flight
            if (this.cameraFlight.isPlaying && Math.random() < 0.01) {
                console.log("Camera position during flight:", this.camera.position.clone());
            }
        }

        if (this.composer && this.viewHelper) {
            this.composer.render();
            this.viewHelper.render(this.renderer);
        }
        else
            if (this.renderer && this.scene && this.camera)
                this.renderer.render(this.scene, this.camera);

        this.viewportGizmo?.update();
        this.viewportGizmo?.render();

        if (this.stats) this.stats.end();
    }

    toggleWireframeMode() {
        this.scene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
                if (Array.isArray(object.material)) {
                    object.material.forEach((material: THREE.Material) => {
                        if (material instanceof THREE.MeshStandardMaterial || material instanceof THREE.MeshLambertMaterial || material instanceof THREE.MeshBasicMaterial) {
                            material.wireframe = !material.wireframe;
                        }
                    });
                } else if (object.material instanceof THREE.MeshStandardMaterial || object.material instanceof THREE.MeshLambertMaterial || object.material instanceof THREE.MeshBasicMaterial) {
                    (object.material as THREE.MeshStandardMaterial | THREE.MeshLambertMaterial | THREE.MeshBasicMaterial).wireframe = !(object.material as THREE.MeshStandardMaterial | THREE.MeshLambertMaterial | THREE.MeshBasicMaterial).wireframe;
                }
            }
        });
        this.updateThreeFrame(); // Re-render the scene
    }

    zoomall(selectedObject: THREE.Object3D) {
        ThreeUtils.zoomObject(this.camera, this.controls, selectedObject);
        this.updateThreeFrame();
    }

    zoomallscene() {
        ThreeUtils.zoomObject(this.camera, this.controls, this.scene.getObjectByName("root"));
        this.updateThreeFrame();
    }

    zoomAllVisible() {
        // Get all objects in the scene
        const visibleObjects: THREE.Object3D[] = [];

        // Helper function to recursively find visible objects
        const findVisibleObjects = (object: THREE.Object3D) => {
            // Skip invisible objects
            if (!object.visible) return;

            // Skip helper objects from basescene.ts
            if (object.userData.isHelper || object.name === 'baseScene') return;

            // Skip helper objects like axes, grid, grass, dirLabels, etc.
            if (object === this.baseScene.axes ||
                object === this.baseScene.grid ||
                object === this.baseScene.grass ||
                (this.baseScene.dirLabels && this.baseScene.dirLabels.some(label => label === object)) ||
                object === this.baseScene.dirLightHelper ||
                object === this.baseScene.hemiLightHelper) {
                return;
            }

            // Add non-empty, non-helper objects (those that have geometry)
            if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
                visibleObjects.push(object);
            }

            // Recursively process children
            object.children.forEach(child => findVisibleObjects(child));
        };

        // Start with the scene object
        findVisibleObjects(this.scene);

        // If no visible objects found, return
        if (visibleObjects.length === 0) {
            console.warn('No visible objects found to zoom to');
            return;
        }

        // Zoom to all visible objects
        ThreeUtils.zoomObject(this.camera, this.controls, visibleObjects, 1.2);
        this.updateThreeFrame();
    }

    setIsometricView(initial: boolean = false) {
        this.cameraManager.setIsometricView(initial, this.project, this.viewportGizmo);
        // Update the reference to the camera after switching
        this.camera = this.cameraManager.camera;
        this.controls = this.cameraManager.controls;
        this.updateThreeFrame();
    }

    resetToPerspective() {
        if (this.previousCamera) {
            this.project.sceneSettings.iso = false;

            // Create a new perspective camera
            const aspect = this.rendererContainer.nativeElement.clientWidth / this.rendererContainer.nativeElement.clientHeight;
            const newCamera = new THREE.PerspectiveCamera(45, aspect, 0.1, 10000);

            // Copy position and rotation from current camera
            newCamera.position.copy(this.camera.position);
            newCamera.rotation.copy(this.camera.rotation);
            newCamera.up.copy(this.camera.up);

            // Store old camera reference and switch to new one
            const oldCamera = this.camera;
            this.camera = newCamera;

            // Update controls
            this.controls.object = this.camera;
            this.controls.target.copy(this.controls.target);
            this.controls.update();

            // Update viewport gizmo
            if (this.viewportGizmo) {
                this.viewportGizmo.camera = this.camera;
                this.viewportGizmo.updateMatrix();
            }

            this.updateThreeFrame();
        }
    }

    updateScene(sceneSettings: any) {
        this.baseScene.updateScene(sceneSettings, this.camera);
        this.updateThreeFrame();
    }

    gridchange(showGrid: boolean) {
        this.baseScene.axes.visible = showGrid;
        this.baseScene.grid.visible = showGrid;
        this.updateThreeFrame();
    }

    handleResize(threecontainerwidth: number, containerHeight: number) {
        // Handle isometric view if active
        if (this.camera instanceof THREE.OrthographicCamera) {
            const aspect = threecontainerwidth / containerHeight;
            const currentZoom = this.camera.zoom;

            // Store initial size only once when first switching to orthographic
            if (this.initialFrustumSize === null) {
                this.initialFrustumSize = this.camera.top - this.camera.bottom;
            }

            // Always use the initial size for calculations
            const size = this.initialFrustumSize;

            // Update all frustum planes using the initial size
            this.camera.left = -size * aspect / 2;
            this.camera.right = size * aspect / 2;
            this.camera.top = size / 2;
            this.camera.bottom = -size / 2;

        } else {
            // Normal perspective mode
            this.camera.aspect = threecontainerwidth / containerHeight;
        }

        this.camera.updateProjectionMatrix();
        this.renderer.setSize(threecontainerwidth, containerHeight);
        this.renderer.autoClear = false;
        this.renderer.setClearColor(0x000000, 0.0);

        this.updateThreeFrame();
    }

    mouseWheel(event: any, threecontainerwidth: number, project: any) {
        // Only process custom zoom if zoom is enabled and pointer zoom is active
        if (project.settings && project.settings.cameraZoom) {
            if (project.settings.cameraZoomPointer) {
                // Custom zoom on pointer behavior
                var container = document.getElementById("threecontainer");
                if (container) {
                    var offsetleft = window.innerWidth - container.clientWidth;
                    var mX = (event.clientX - offsetleft) / (container.clientWidth) * 2 - 1;
                    var mY = -event.clientY / container.clientHeight * 2 + 1;

                    if (this.camera instanceof THREE.OrthographicCamera) {
                        // Handle orthographic camera differently - use raycasting
                        const raycaster = new THREE.Raycaster();
                        const mouse = new THREE.Vector2(mX, mY);

                        // Store mouse position in world coordinates before zoom
                        raycaster.setFromCamera(mouse, this.camera);
                        const initialWorldPosition = new THREE.Vector3();
                        const planeNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(this.camera.quaternion);
                        const plane = new THREE.Plane(planeNormal, 0);
                        raycaster.ray.intersectPlane(plane, initialWorldPosition);

                        // Determine zoom factor based on scroll direction
                        const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
                        const previousZoom = this.camera.zoom;

                        // Apply zoom
                        this.camera.zoom *= zoomFactor;
                        this.camera.updateProjectionMatrix();

                        // Calculate new world position after zoom
                        raycaster.setFromCamera(mouse, this.camera);
                        const newWorldPosition = new THREE.Vector3();
                        raycaster.ray.intersectPlane(plane, newWorldPosition);

                        // Adjust camera position to keep mouse point fixed
                        const movementOffset = initialWorldPosition.clone().sub(newWorldPosition);
                        this.camera.position.add(movementOffset);
                        this.controls.target.add(movementOffset);

                        // Update controls
                        this.controls.update();
                    } else {
                        // Very simple, non-rotating perspective zoom
                        const zoomFactor = event.deltaY < 0 ? 0.9 : 1.1;

                        // Get the direction vector from camera to target
                        const direction = new THREE.Vector3();
                        direction.subVectors(this.controls.target, this.camera.position);

                        // Get current distance to target
                        const distance = direction.length();

                        // Create normalized direction of mouse pointer
                        const pointerDir = new THREE.Vector3(mX, mY, 0.5);
                        pointerDir.unproject(this.camera);
                        pointerDir.sub(this.camera.position).normalize();

                        // Calculate new position - move along pointer direction
                        const offset = pointerDir.clone().multiplyScalar(distance * (1 - zoomFactor));
                        this.camera.position.add(offset);

                        // Update controls
                        this.controls.update();
                    }

                    // Stop the event from propagating to OrbitControls
                    event.stopPropagation();
                    event.preventDefault();
                }
            }
            // If zoom on pointer is disabled, OrbitControls handles zooming automatically
        }

        // Always update the frame to prevent white flashes
        this.updateThreeFrame();
    }

    dispose() {
        if (this.cameraFlight) {
            this.cameraFlight.dispose();
        }
        // Add other cleanup as needed
    }
} 